export type ClientStage = 'CLIENT' | 'PROSPECT';
export interface ClientFields {
  readonly stage: ClientStage;
  readonly displayName: string;
  readonly email: string | null;
  readonly siren: string | null;
  readonly siret: string | null;
  readonly phone: string | null;
  readonly websiteUrl: string | null;
  readonly websiteUrls?: readonly string[];
  readonly postalAddress: string | null;
  readonly notes: string | null;
}
export interface ClientRecord extends ClientFields {
  readonly id: string;
  readonly recipientGroupIds: readonly string[];
  readonly origin: 'MANUAL' | 'FREEBE_CONFIRMED' | 'FREEBE_LEGACY_UNREVIEWED';
  readonly version: number;
  readonly hasLogo: boolean;
  readonly sources: readonly {
    readonly id: string;
    readonly displayName: string;
    readonly lastImportedAt: string;
    readonly differs: boolean;
  }[];
}
export interface ClientSource {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly siren: string | null;
  readonly siret: string | null;
  readonly linkedClientId: string | null;
  readonly linkedClient?: ClientRecord | null;
  readonly firstImportedAt: string;
}
export interface ClientDirectory {
  readonly clients: readonly ClientRecord[];
  readonly prospects: readonly ClientRecord[];
  readonly freebeCandidates: readonly ClientSource[];
  readonly clientCount: number;
  readonly prospectCount: number;
  readonly sourceCount: number;
  readonly lastImportedAt: string | null;
  readonly historicalCount: number;
}

export class ClientDirectoryRequestError extends Error {
  public constructor(
    public readonly code: string,
    public readonly candidates: readonly { readonly id: string; readonly displayName: string }[],
  ) {
    super(code);
    this.name = 'ClientDirectoryRequestError';
  }
}

async function request<T>(
  path: string,
  method = 'GET',
  csrfToken?: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch('/api/clients' + path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const error: unknown = await response.json().catch(() => null);
    const message = error && typeof error === 'object' && 'message' in error ? error.message : null;
    const code =
      message && typeof message === 'object' && 'code' in message
        ? String(message.code)
        : error && typeof error === 'object' && 'code' in error
          ? String(error.code)
          : 'REQUEST_FAILED';
    const candidates =
      message &&
      typeof message === 'object' &&
      'candidates' in message &&
      Array.isArray(message.candidates)
        ? (message.candidates as unknown[]).filter(
            (candidate): candidate is { id: string; displayName: string } =>
              !!candidate &&
              typeof candidate === 'object' &&
              'id' in candidate &&
              typeof candidate.id === 'string' &&
              'displayName' in candidate &&
              typeof candidate.displayName === 'string',
          )
        : [];
    throw new ClientDirectoryRequestError(code, candidates);
  }
  return response.json() as Promise<T>;
}

export const clientDirectoryApi = {
  search(
    query = '',
    page = 0,
    mode: 'clients' | 'prospects' | 'historical' = 'clients',
  ): Promise<ClientDirectory> {
    return request<ClientDirectory>(
      '?q=' + encodeURIComponent(query) + '&page=' + page + '&mode=' + mode,
    );
  },
  createManual(
    fields: ClientFields,
    csrfToken: string,
    confirmedDuplicateIds: readonly string[] = [],
    recipientGroupId: string | null = null,
  ): Promise<ClientRecord> {
    return request<ClientRecord>('', 'POST', csrfToken, {
      ...fields,
      confirmedDuplicateIds,
      recipientGroupId,
      confirmedClientRelationship: true,
    });
  },
  createFromFreebe(
    sourceReferenceId: string,
    fields: ClientFields,
    csrfToken: string,
    confirmedDuplicateIds: readonly string[] = [],
    recipientGroupId: string | null = null,
  ): Promise<ClientRecord> {
    return request<ClientRecord>('', 'POST', csrfToken, {
      sourceReferenceId,
      displayName: fields.displayName,
      email: fields.email,
      phone: fields.phone,
      websiteUrl: fields.websiteUrl,
      websiteUrls: fields.websiteUrls,
      postalAddress: fields.postalAddress,
      notes: fields.notes,
      confirmedDuplicateIds,
      recipientGroupId,
      confirmedClientRelationship: true,
    });
  },
  update(
    client: ClientRecord,
    fields: ClientFields,
    csrfToken: string,
    confirmedDuplicateIds: readonly string[] = [],
  ): Promise<ClientRecord> {
    return request<ClientRecord>('/' + encodeURIComponent(client.id), 'PATCH', csrfToken, {
      ...fields,
      expectedVersion: client.version,
      confirmedDuplicateIds,
    });
  },
  requalify(client: ClientRecord, target: ClientStage, csrfToken: string): Promise<ClientRecord> {
    return request<ClientRecord>(
      '/' + encodeURIComponent(client.id) + '/qualification',
      'POST',
      csrfToken,
      {
        expectedVersion: client.version,
        target,
        confirmed: true,
      },
    );
  },
  attachRecipientGroup(
    client: ClientRecord,
    recipientGroupId: string,
    csrfToken: string,
  ): Promise<ClientRecord> {
    return request<ClientRecord>(
      '/' + encodeURIComponent(client.id) + '/recipient-group',
      'POST',
      csrfToken,
      {
        expectedVersion: client.version,
        recipientGroupId,
        confirmed: true,
      },
    );
  },
  attach(
    client: ClientRecord,
    sourceReferenceId: string,
    csrfToken: string,
  ): Promise<ClientRecord> {
    return request<ClientRecord>(
      '/' + encodeURIComponent(client.id) + '/source',
      'POST',
      csrfToken,
      { sourceReferenceId, expectedVersion: client.version },
    );
  },
  confirmLegacy(client: ClientRecord, csrfToken: string): Promise<ClientRecord> {
    return request<ClientRecord>(
      '/' + encodeURIComponent(client.id) + '/confirm',
      'POST',
      csrfToken,
      { expectedVersion: client.version, confirmedClientRelationship: true },
    );
  },
  async saveLogo(client: ClientRecord, file: File, csrfToken: string): Promise<ClientRecord> {
    const response = await fetch('/api/clients/' + encodeURIComponent(client.id) + '/logo', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': file.type,
        'X-CSRF-Token': csrfToken,
        'X-Client-Version': String(client.version),
      },
      body: file,
    });
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      const message =
        payload && typeof payload === 'object' && 'message' in payload ? payload.message : null;
      const code =
        message && typeof message === 'object' && 'code' in message
          ? String(message.code)
          : 'REQUEST_FAILED';
      throw new ClientDirectoryRequestError(code, []);
    }
    return response.json() as Promise<ClientRecord>;
  },
};
